import {
  Button,
  Flex,
  Heading,
  Icon,
  MenuButton,
  MenuDivider,
  Popover,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useTheme,
  useToast,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import firebase from 'firebase/app';
import { motion } from 'framer-motion';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import {
  Calendar,
  Info,
  MoreHorizontal,
  Play,
  ThumbsDown,
  ThumbsUp,
  Video,
  VideoOff,
} from 'react-feather';
import { Menu, MenuItem } from '../../components/styled-chakra/Menu';
import { FirebaseConfig, FirebaseCourseItem } from '../../types/types';
import { canRegisterCourse } from '../../utils/canRegisterCourse';
import { getIcs } from '../../utils/getIcs';
import { getRemainingRegistrants } from '../../utils/getRemainingRegistrants';
import { isShortlyBeforeCourse } from '../../utils/isShortlyBeforeCourse';
import { registerCourse } from '../../utils/registerCourse';
import { CourseItemPopover } from './CourseItemPopover';
import { LiveTag } from './LiveTag';

const Stroke = styled(Text)`
  -webkit-text-stroke-width: 2px;
  -webkit-text-stroke-color: ${(props: any) => props.theme.colors.brand['500']};
  user-select: none;
`;

const AnimatedFlex = motion(Flex);
const containerVariants = {
  hidden: {
    y: -10,
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: { delay: 0.1 },
  },
  exit: {
    y: 10,
    opacity: 0,
    transition: { duration: 0.1 },
  },
};

interface NewCourseItemProps {
  course: FirebaseCourseItem;
  currentUser: firebase.User;
  history: any;
  config: FirebaseConfig;
  category?: string;
}

export const CourseItem: React.FC<NewCourseItemProps> = ({
  course,
  currentUser,
  history,
  config,
  category,
}) => {
  const toast = useToast();
  const [loading, setIsLoading] = useState(false);
  const startMomentID = moment(course.startMoment).unix().toString();
  const theme = useTheme();
  const isRegistered = useMemo(
    () =>
      course.dates &&
      course.dates[startMomentID] &&
      course.dates[startMomentID].find(
        (element) => element.id === currentUser.uid
      ),
    [course]
  );

  const remainingRegistrants = getRemainingRegistrants(course, config);

  const isShortlyBefore = isShortlyBeforeCourse(course, config);

  const onIcsClick = async () => {
    try {
      setIsLoading(true);
      await getIcs(course);
      setIsLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setIsLoading(false);
    }
  };

  const onRegisterClick = async () => {
    try {
      setIsLoading(true);
      await registerCourse(course, currentUser, isRegistered);
      toast({
        title: `${isRegistered ? 'Stornierung' : 'Registrierung'} Erfolgreich`,
        duration: 4000,
        status: 'success',
      });
      setIsLoading(false);
    } catch (e) {
      toast({
        title: 'Fehler bei der Kursregistrierung',
        duration: 4000,
        status: 'error',
      });
      // eslint-disable-next-line no-console
      console.log(e);
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      id={`${moment(course.startMoment).format('DDMMyy')}`}
    >
      <AnimatedFlex
        borderRadius="xl"
        backgroundImage={`url('${course.img}')`}
        h={['350px', '300px', '350px']}
        backgroundPosition="center"
        backgroundSize="cover"
        p={4}
        direction="column"
        justify="space-between"
        position="relative"
        overflow="hidden"
        boxShadow="md"
        zIndex={5}
        animate={{
          boxShadow: course.isNow
            ? [
                `0px 0px 10px ${theme.colors.green['500']}`,
                `0px 0px 10px ${theme.colors.red['500']}`,
                `0px 0px 10px ${theme.colors.blue['500']}`,
                `0px 0px 10px ${theme.colors.yellow['500']}`,
                `0px 0px 10px ${theme.colors.green['500']}`,
              ]
            : '',
        }}
        transition={{ ease: 'easeInOut', duration: 3, repeat: Infinity }}
      >
        <Flex
          w="100%"
          justify="space-between"
          position="relative"
          zIndex="dropdown"
        >
          <Popover placement="bottom-start">
            <PopoverTrigger>
              <Button
                colorScheme="brand"
                aria-label="Info"
                leftIcon={<Icon as={Info} />}
              >
                Infos
              </Button>
            </PopoverTrigger>
            <Portal>
              <CourseItemPopover
                course={course}
                category={category}
                config={config}
              />
            </Portal>
          </Popover>
          <Menu
            menuProps={{ flip: false, placement: 'bottom-end' }}
            triggerComponent={
              <MenuButton
                ml={3}
                as={Button}
                colorScheme="brand"
                aria-label="Kursmenü"
                leftIcon={<Icon as={MoreHorizontal} />}
                isLoading={loading}
              >
                Aktionen
              </MenuButton>
            }
          >
            {!isShortlyBefore &&
              course.canRegister &&
              canRegisterCourse(course, config) && (
                <>
                  <MenuItem
                    isDisabled={remainingRegistrants <= 0 && !isRegistered}
                    onClick={onRegisterClick}
                    icon={<Icon as={isRegistered ? ThumbsDown : ThumbsUp} />}
                  >
                    {(isRegistered && 'Reservierung stornieren') ||
                      (remainingRegistrants <= 0 &&
                        'Kurs bereits ausgebucht') ||
                      'Platz im Studio sichern'}
                  </MenuItem>
                  <MenuDivider />
                </>
              )}
            <MenuItem onClick={onIcsClick} icon={<Icon as={Calendar} />}>
              In eigenen Kalender
            </MenuItem>
          </Menu>
        </Flex>
        <Flex justify="center" w="100%">
          {isShortlyBefore && course.isOnline && (
            <Button
              shadow="xl"
              size="lg"
              leftIcon={<Icon as={Play} />}
              boxShadow="xl"
              colorScheme="brand"
              onClick={() =>
                history.push(`/public-course/${course.uid}/${course.title}`)
              }
              zIndex="99"
            >
              Zum Online-Kurs
            </Button>
          )}
        </Flex>
        <Flex direction="column" zIndex={4} textOverflow="ellipsis">
          <Flex align="center">
            <Tooltip
              px={2}
              py={1}
              borderRadius="md"
              hasArrow={true}
              bg="white"
              color="current"
              placement="top"
              label={course.isOnline ? 'Online-Kurs' : 'kein Online-Kurs'}
              aria-label="Online-Kurs"
            >
              <Icon
                mt={1}
                mr={2}
                as={course.isOnline ? Video : VideoOff}
                size="1em"
                color="white"
              />
            </Tooltip>

            <Heading
              as="h4"
              size="md"
              color="#fff"
              textShadow="0px 3px 10px #000000"
            >
              {moment(course.startMoment).format('LT')}
              {' - '}
              {moment(course.endMoment).format('LT')}
            </Heading>
          </Flex>

          <Heading
            as="h3"
            size="xl"
            color="#fff"
            mr={10}
            textShadow="0px 3px 15px #000000"
          >
            {course.title}
          </Heading>
        </Flex>
        <Flex
          zIndex={2}
          position="absolute"
          bottom="0"
          left="0"
          h="100%"
          bgGradient="linear(to-b, #00000066, #00000000, #00000000, #00000000, #00000033, #000000ee)"
          w="100%"
        />
        <Stroke
          zIndex={3}
          fontSize="135px"
          position="absolute"
          bottom="0"
          left="-5px"
          lineHeight="0.7"
          color="transparent"
          fontWeight="bold"
          whiteSpace="nowrap"
          textTransform="uppercase"
        >
          {course.title}
        </Stroke>
        {(course.isNow || isShortlyBefore) && <LiveTag isLive={course.isNow} />}
      </AnimatedFlex>
    </motion.div>
  );
};
